import { useTranslation } from 'react-i18next'
import type { AuditReport } from '@/lib/api/amazon-listing'

function ScoreDelta({ current, previous }: { current: number; previous: number }) {
  const delta = current - previous
  if (delta === 0) return null
  const color = delta > 0 ? 'text-green-400' : 'text-red-400'
  const arrow = delta > 0 ? '\u2191' : '\u2193'
  return <span className={`text-xs font-medium ${color} ml-1`}>{delta > 0 ? '+' : ''}{delta} {arrow}</span>
}

const toCamel = (s: string) => s.replace(/_([a-z])/g, (_, c: string) => c.toUpperCase())

const scoreColor = (score: number) => {
  if (score >= 80) return 'from-green-400 to-green-600'
  if (score >= 60) return 'from-yellow-400 to-yellow-600'
  return 'from-red-400 to-red-600'
}

interface ProductScoreGridProps {
  scores: AuditReport['scores']
  previousScores?: Partial<AuditReport['scores']> | Record<string, number>
}

export function ProductScoreGrid({ scores, previousScores }: ProductScoreGridProps) {
  const { t } = useTranslation()
  return (
    <div className="grid grid-cols-3 sm:grid-cols-4 md:flex gap-2 md:gap-4 mt-4 md:mt-6">
      {Object.entries(scores)
        .filter(([, value]) => typeof value === 'number')
        .map(([key, value]) => (
          <div key={key} className="bg-white/5 rounded-xl p-2 md:p-3 md:min-w-[100px]">
            <div className={`text-lg md:text-2xl font-bold bg-gradient-to-r ${scoreColor(value as number)} bg-clip-text text-transparent`}>
              {value as number}
              {previousScores && typeof previousScores[key as keyof typeof previousScores] === 'number' && (
                <ScoreDelta current={value as number} previous={previousScores[key as keyof typeof previousScores] as number} />
              )}
            </div>
            <div className="text-xs text-neutral-400 mt-1">
              {t(`amazonListing.scores.${toCamel(key)}`, { defaultValue: key })}
            </div>
          </div>
        ))}
    </div>
  )
}
